/**
 * Meta Pixel Helper Functions
 * 
 * Handles Meta/Facebook Pixel tracking with consent awareness and COPPA compliance.
 * Supports event deduplication for future CAPI implementation.
 */

import { ANALYTICS_CONFIG, META_EVENT_MAPPING, type AnalyticsEventName, sanitizeEventParams } from './analyticsConfig';
import { logger } from "@/lib/logger";

declare global {
  interface Window {
    fbq: ((...args: unknown[]) => void) & { 
      callMethod?: (...args: unknown[]) => void;
      queue: unknown[];
      loaded: boolean;
      version: string;
      push: (...args: unknown[]) => void;
    };
    _fbq: unknown;
  }
}

/**
 * Generate a unique event ID for deduplication
 * Used when implementing both Pixel + CAPI
 */
export function generateEventId(): string {
  return `${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
}

/**
 * Check if Meta Pixel is loaded
 */
export function isMetaPixelLoaded(): boolean {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
}

/**
 * Initialize Meta Pixel (called once on page load after consent)
 */
export function initializeMetaPixel(pixelId: string): void {
  if (!pixelId || typeof window === 'undefined') {
    return;
  }

  // Don't reinitialize if already loaded
  if (isMetaPixelLoaded()) {
    return;
  }

  // Meta Pixel base code (simplified for TypeScript)
  const f = window;
  const b = document;
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fbqFunc: any = function(...args: unknown[]) {
    if (fbqFunc.callMethod) {
      fbqFunc.callMethod.apply(fbqFunc, args);
    } else {
      fbqFunc.queue.push(args);
    }
  };
  
  fbqFunc.push = fbqFunc;
  fbqFunc.loaded = true;
  fbqFunc.version = '2.0';
  fbqFunc.queue = [];
  
  f.fbq = fbqFunc;
  if (!f._fbq) f._fbq = fbqFunc;
  
  // Load the script
  const script = b.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  const firstScript = b.getElementsByTagName('script')[0];
  firstScript?.parentNode?.insertBefore(script, firstScript);

  // Initialize with pixel ID
  window.fbq('init', pixelId);
  
  if (ANALYTICS_CONFIG.debugMode) {
    logger.debug('[Meta Pixel] Initialized with ID:', pixelId);
  }
}

/**
 * Track a Meta Pixel event
 * 
 * @param eventName - Internal event name (will be mapped to Meta standard event)
 * @param params - Event parameters (will be sanitized)
 * @param options - Additional options
 */
export function trackMetaEvent(
  eventName: AnalyticsEventName | string,
  params?: Record<string, unknown>,
  options?: {
    eventId?: string;
    isCustomEvent?: boolean;
  }
): void {
  if (!isMetaPixelLoaded()) {
    if (ANALYTICS_CONFIG.debugMode) {
      logger.debug('[Meta Pixel] Not loaded, skipping event:', eventName);
    }
    return;
  }

  // Map to Meta standard event if available
  const metaEventName = META_EVENT_MAPPING[eventName as AnalyticsEventName] || eventName;
  const isStandardEvent = Object.values(META_EVENT_MAPPING).includes(metaEventName);

  // Sanitize parameters
  const sanitizedParams = params ? sanitizeEventParams(params) : {};

  // Add event ID for deduplication (useful for CAPI)
  if (options?.eventId) {
    sanitizedParams.eventID = options.eventId;
  }

  try {
    if (isStandardEvent && !options?.isCustomEvent) {
      // Standard Meta event
      window.fbq('track', metaEventName, sanitizedParams);
    } else {
      // Custom event
      window.fbq('trackCustom', metaEventName, sanitizedParams);
    }

    if (ANALYTICS_CONFIG.debugMode) {
      logger.debug('[Meta Pixel] Event tracked', { metaEventName, sanitizedParams });
    }
  } catch (error) {
    console.debug('[Meta Pixel] Error tracking event:', error);
  }
}

/**
 * Track Meta PageView event
 */
export function trackMetaPageView(): void {
  if (!isMetaPixelLoaded()) {
    return;
  }

  try {
    window.fbq('track', 'PageView');
    
    if (ANALYTICS_CONFIG.debugMode) {
      logger.debug('[Meta Pixel] PageView tracked');
    }
  } catch (error) {
    console.debug('[Meta Pixel] Error tracking PageView:', error);
  }
}

/**
 * Disable Meta Pixel tracking (for consent revocation)
 */
export function disableMetaPixel(): void {
  if (typeof window !== 'undefined' && isMetaPixelLoaded()) {
    // Meta doesn't have a built-in disable, but we can avoid further tracking
    // by not calling fbq functions when consent is revoked
    if (ANALYTICS_CONFIG.debugMode) {
      logger.debug('[Meta Pixel] Tracking disabled (consent revoked)');
    }
  }
}

export default {
  initializeMetaPixel,
  trackMetaEvent,
  trackMetaPageView,
  isMetaPixelLoaded,
  generateEventId,
  disableMetaPixel,
};
